import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { triggerRefresh } from "./refreshSlice";
import { toast } from "../common_components/Toast/ToastUtils";
// ==========================================================================
// ✅ ASYNC THUNKS
// ==========================================================================
export const fetchAccountPerformance = createAsyncThunk("accountPerformance/fetch", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/account-performance", payload, {
            Authorization: token || "",
        });
        if (response && "data" in response && response?.data) {
            const responseData = response.data;
            if (responseData?.success) {
                return responseData?.data;
            }
            else {
                return rejectWithValue(responseData?.message || "Failed to fetch account performance");
            }
        }
        else {
            return rejectWithValue("Failed to fetch account performance");
        }
    }
    catch (error) {
        console.error("Account Performance API Error:", error);
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred while fetching account performance");
    }
});
export const refreshAccountPerformance = createAsyncThunk("accountPerformance/refreshAccountPerformance", async (payload, { dispatch, rejectWithValue }) => {
    let dateRange = [];
    if (payload.startDate && payload.endDate) {
        dateRange = [{ startDate: payload.startDate, endDate: payload.endDate }];
    }
    else if (payload.month) {
        dateRange = [{ startDate: `${payload.month}-01`, endDate: `${payload.month}-01` }];
    }
    const result = await dispatch(triggerRefresh({
        dateRange,
        moduleKey: payload.moduleKey || "account_performance",
        ...(payload.clientId && { clientId: payload.clientId }),
        ...(payload.clientIds && { clientIds: payload.clientIds }),
        ...(payload.submodule && { submodule: payload.submodule }),
    }));
    if (triggerRefresh.fulfilled.match(result)) {
        const failedNetworks = result.payload?.data?.failed_networks || [];
        if (failedNetworks.length > 0) {
            const failedNames = [...new Map(failedNetworks.map((n) => [n.network, n.error])).entries()]
                .map(([name, err]) => {
                let eStr = typeof err === 'string' ? err : err?.message;
                if (eStr && eStr.toLowerCase().includes('time'))
                    eStr = 'Timeout';
                return `${name.charAt(0).toUpperCase() + name.slice(1)}${eStr ? ` (${eStr})` : ''}`;
            })
                .join(', ');
            toast.warning(`Account Performance refresh completed.\nFailed to refresh: ${failedNames}.`);
        }
        return (result.payload?.data ?? result.payload);
    }
    return rejectWithValue(result.payload || "Failed to refresh account performance");
});
// ==========================================================================
// ✅ INITIAL STATE
// ==========================================================================
const initialState = {
    clients: [],
    totalSummary: null,
    pagination: null,
    loading: false,
    error: null,
    hasAppliedCache: false,
    // Default filters
    month: "",
    accountManagerId: "",
    clientType: "All",
    page: 1,
    limit: 500, // Large enough to show every active client on one page — pagination controls are hidden by design.
    // ✅ Refresh
    refreshLoading: false,
    refreshError: null,
    refreshResult: null,
    lastUpdatedAt: undefined,
    overallStatus: null,
    // Sorting
    sortBy: "revenueGoal",
    sortOrder: "desc",
};
// ==========================================================================
// ✅ SLICE
// ==========================================================================
const accountPerformanceSlice = createSlice({
    name: "accountPerformance",
    initialState,
    reducers: {
        setMonth: (state, action) => {
            state.month = action.payload;
            state.page = 1; // Reset page on filter change
        },
        setAccountManagerId: (state, action) => {
            state.accountManagerId = action.payload;
            state.page = 1;
        },
        setClientType: (state, action) => {
            state.clientType = action.payload;
            state.page = 1;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setLimit: (state, action) => {
            state.limit = action.payload;
            state.page = 1;
        },
        setSortConfig: (state, action) => {
            state.sortBy = action.payload.sortBy;
            state.sortOrder = action.payload.sortOrder;
            state.page = 1;
        },
        clearAccountPerformance: (state) => {
            state.clients = [];
            state.totalSummary = null;
            state.pagination = null;
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAccountPerformance.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAccountPerformance.fulfilled, (state, action) => {
            state.loading = false;
            state.clients = action.payload.clients || [];
            state.totalSummary = action.payload.total_summary || null;
            state.pagination = action.payload.pagination || null;
            state.error = null;
            state.lastUpdatedAt = action.payload.lastUpdatedAt || null;
            state.overallStatus = action.payload.overall_status || "not_found";
            // ⚡ ONE-TIME sync on initial load: restore month from Redis cache.
            // Gated on the *first* response, whether or not it had a cache, so a
            // later response (e.g. after saveToUserCache populates the cache for
            // the first time) can never re-trigger this and clobber the user's
            // own subsequent month selection.
            if (!state.hasAppliedCache) {
                state.hasAppliedCache = true;
                if (action.payload.cache) {
                    state.cache = action.payload.cache;
                    const cachedMonth = action.payload.cache.month;
                    // Only apply it if the user hasn't already picked a different
                    // month while this (initial) request was in flight.
                    if (cachedMonth && state.month === action.meta.arg.month && cachedMonth !== state.month) {
                        state.month = cachedMonth;
                        state.page = 1;
                    }
                }
            }
        })
            .addCase(fetchAccountPerformance.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch account performance";
        })
            // ✅ Refresh Account Performance (schedule)
            .addCase(refreshAccountPerformance.pending, (state) => {
            state.refreshLoading = true;
            state.refreshError = null;
            state.refreshResult = null;
        })
            .addCase(refreshAccountPerformance.fulfilled, (state, action) => {
            state.refreshLoading = false;
            state.refreshResult = action.payload;
        })
            .addCase(refreshAccountPerformance.rejected, (state, action) => {
            state.refreshLoading = false;
            state.refreshError = action.payload || "Failed to refresh account performance";
        });
    },
});
// ==========================================================================
// ✅ EXPORTS
// ==========================================================================
export const { setMonth, setAccountManagerId, setClientType, setPage, setLimit, setSortConfig, clearAccountPerformance, clearError, } = accountPerformanceSlice.actions;
// ==========================================================================
// ✅ SELECTORS
// ==========================================================================
export const selectAccountPerformanceClients = (state) => state.accountPerformance.clients;
export const selectAccountPerformanceLoading = (state) => state.accountPerformance.loading;
export const selectAccountPerformanceError = (state) => state.accountPerformance.error;
export const selectTotalSummary = (state) => state.accountPerformance.totalSummary;
export const selectPagination = (state) => state.accountPerformance.pagination;
export const selectMonth = (state) => state.accountPerformance.month;
export const selectAccountManagerId = (state) => state.accountPerformance.accountManagerId;
export const selectClientType = (state) => state.accountPerformance.clientType;
export const selectPage = (state) => state.accountPerformance.page;
export const selectLimit = (state) => state.accountPerformance.limit;
export const selectRefreshLoading = (state) => state.accountPerformance.refreshLoading;
export const selectRefreshError = (state) => state.accountPerformance.refreshError;
export const selectRefreshResult = (state) => state.accountPerformance.refreshResult;
export const selectLastUpdatedAt = (state) => state.accountPerformance.lastUpdatedAt;
export const selectOverallStatus = (state) => state.accountPerformance.overallStatus;
export default accountPerformanceSlice.reducer;
